import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, ArrowUp, History, PenLine, LifeBuoy } from 'lucide-react';
import { CrisisFooter } from '@/components/CrisisFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChatPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Elevated safety banner shown when the AI screener flags a message as crisis
  // or high-distress. Auto-hides when the user sends another safe message.
  const [safetyAlert, setSafetyAlert] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    axios.get(`${API}/chat/sessions`, { headers: authHeaders })
      .then((res) => setSessions(res.data.sessions || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openSession = async (sid) => {
    setShowHistory(false);
    try {
      const res = await axios.get(`${API}/chat/history`, {
        params: { session_id: sid },
        headers: authHeaders,
      });
      const loaded = (res.data.messages || [])
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role, content: m.content }));
      setMessages(loaded);
      setSessionId(sid);
    } catch {
      toast.error('Could not open that conversation');
    }
  };

  const startNewConversation = () => {
    setShowHistory(false);
    setMessages([]);
    setSessionId(null);
    inputRef.current?.focus();
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || loading) return;

    const userMessage = inputText.trim();
    setInputText('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat/message`,
        { message: userMessage, session_id: sessionId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => [...prev, { role: 'assistant', content: response.data.response }]);
      // Surface elevated safety banner when the screener flags this turn.
      // 'crisis' shows the persistent alert; 'distressed' clears any prior banner
      // without popping a new one; 'safe' also clears.
      const severity = response.data.safety_severity;
      if (severity === 'crisis') {
        setSafetyAlert('crisis');
      } else {
        setSafetyAlert(null);
      }
      if (response.data.session_id && !sessionId) {
        setSessionId(response.data.session_id);
        axios.get(`${API}/chat/sessions`, { headers: authHeaders })
          .then((res) => setSessions(res.data.sessions || []))
          .catch(() => {});
      }
    } catch (error) {
      toast.error('That message didn\'t go through. Try again in a moment.');
      setMessages((prev) => prev.slice(0, -1));
      setInputText(userMessage);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const firstName = user?.preferred_name || user?.name?.split(' ')[0] || '';

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FBF9F6' }} data-testid="chat-page">
      {/* Header */}
      <header className="sticky top-0 z-10" style={{ backgroundColor: 'rgba(251,249,246,0.92)', backdropFilter: 'blur(12px)', borderBottom: '1px solid #EAE4DC' }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/sanctuary')}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 hover:bg-[#F7F1EE]"
            aria-label="Back to sanctuary"
            data-testid="back-to-sanctuary-button"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} style={{ color: '#1A1A2E' }} />
          </button>
          <div className="flex items-center gap-3 flex-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#C9A84C' }} />
            <h1 className="text-xl" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
              Sage
            </h1>
          </div>
          {(messages.length > 0 || sessionId) && (
            <button
              onClick={startNewConversation}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 hover:bg-[#F7F1EE]"
              aria-label="Start a new conversation"
              title="New conversation"
              data-testid="new-conversation-button"
            >
              <PenLine className="w-[18px] h-[18px]" strokeWidth={1.5} style={{ color: '#4A4A5A' }} />
            </button>
          )}
          {sessions.length > 0 && (
            <button
              onClick={() => setShowHistory((v) => !v)}
              className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 hover:bg-[#F7F1EE]"
              aria-label="Past conversations"
              aria-expanded={showHistory}
              title="Past conversations"
              data-testid="chat-history-button"
            >
              <History className="w-[18px] h-[18px]" strokeWidth={1.5} style={{ color: showHistory ? '#1A1A2E' : '#4A4A5A' }} />
            </button>
          )}
        </div>
        {showHistory && (
          <div className="max-w-3xl mx-auto px-6 pb-4 animate-fadeIn" data-testid="chat-history-panel">
            <p className="text-[11px] tracking-[0.25em] uppercase mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
              Past conversations
            </p>
            <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#FFFFFF', border: '1px solid #EAE4DC' }}>
              <div className="max-h-72 overflow-y-auto divide-y" style={{ borderColor: '#F0EBE4' }}>
                {sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => openSession(s.session_id)}
                    className="w-full text-left px-5 py-3.5 transition-colors duration-150 hover:bg-[#F9F4F0]"
                    style={s.session_id === sessionId ? { backgroundColor: '#F7F1EE' } : {}}
                    data-testid={`chat-session-${s.session_id}`}
                  >
                    <p className="text-[14px] truncate" style={{ fontFamily: 'Jost, sans-serif', color: '#1A1A2E' }}>
                      {s.title || s.preview}
                    </p>
                    <p className="text-[12px] mt-0.5" style={{ fontFamily: 'Jost, sans-serif', color: '#8A8798' }}>
                      {s.last_message_at ? new Date(s.last_message_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}
                      {s.message_count ? ` · ${s.message_count} messages` : ''}
                    </p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-6 pt-12 pb-8">
          {safetyAlert === 'crisis' && (
            <div
              className="mb-8 rounded-2xl border p-5 flex items-start gap-4 animate-slideUp"
              style={{ backgroundColor: '#FDF4F3', borderColor: '#E8B4B8' }}
              role="alert"
              data-testid="chat-crisis-banner"
            >
              <LifeBuoy className="w-6 h-6 mt-0.5 flex-shrink-0" style={{ color: '#B85450' }} strokeWidth={1.5} />
              <div className="flex-1">
                <p className="text-[15px] font-medium mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
                  You don't have to be in this alone.
                </p>
                <p className="text-[14px] leading-relaxed mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}>
                  If any part of you is thinking about hurting yourself, please reach out — call or text
                  {' '}<strong>988</strong> (Suicide & Crisis Lifeline), text <strong>HOME to 741741</strong>{' '}
                  (Crisis Text Line), or call <strong>1-800-799-7233</strong> for domestic violence support.
                </p>
                <button
                  onClick={() => setSafetyAlert(null)}
                  className="text-[12px] tracking-[0.15em] uppercase underline"
                  style={{ fontFamily: 'Jost, sans-serif', color: '#B85450' }}
                  data-testid="chat-crisis-dismiss"
                >
                  I've read this
                </button>
              </div>
            </div>
          )}
          {messages.length === 0 && (
            <div className="py-16 animate-slideUp" data-testid="chat-empty-state">
              <p className="text-xs tracking-[0.25em] uppercase mb-6" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
                A private conversation
              </p>
              <h2 className="text-3xl sm:text-4xl leading-snug mb-6" style={{ fontFamily: 'Playfair Display, serif', color: '#1A1A2E' }}>
                {firstName ? `What's actually going on, ${firstName}?` : "What's actually going on?"}
              </h2>
              <p className="text-base leading-relaxed max-w-md" style={{ fontFamily: 'Jost, sans-serif', color: '#4A4A5A' }}>
                Bring me the message you don't know how to answer, the situation you
                can't read clearly, or just the day you've had. I know your history,
                and I pay attention.
              </p>
            </div>
          )}

          <div className="space-y-10">
            {messages.map((message, idx) => (
              message.role === 'user' ? (
                <div key={`msg-${idx}`} className="flex justify-end animate-slideUp" data-testid="chat-message-user">
                  <div
                    className="max-w-[85%] sm:max-w-[75%] rounded-2xl rounded-br-md px-5 py-3.5"
                    style={{ backgroundColor: '#F7F1EE', border: '1px solid #F0EBE4' }}
                  >
                    <p className="leading-relaxed whitespace-pre-wrap text-[15px]" style={{ fontFamily: 'Jost, sans-serif', color: '#1A1A2E' }}>
                      {message.content}
                    </p>
                  </div>
                </div>
              ) : (
                <div key={`msg-${idx}`} className="animate-slideUp" data-testid="chat-message-assistant">
                  <p className="text-[11px] tracking-[0.25em] uppercase mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
                    Sage
                  </p>
                  <div className="pl-4" style={{ borderLeft: '2px solid #EAE4DC' }}>
                    <p className="leading-[1.8] whitespace-pre-wrap text-[16px]" style={{ fontFamily: 'Jost, sans-serif', color: '#2E2E42' }}>
                      {message.content}
                    </p>
                  </div>
                </div>
              )
            ))}

            {loading && (
              <div className="animate-fadeIn" data-testid="chat-loading-state">
                <p className="text-[11px] tracking-[0.25em] uppercase mb-3" style={{ fontFamily: 'Jost, sans-serif', color: '#C9A84C' }}>
                  Sage
                </p>
                <p className="pl-4 italic text-[15px]" style={{ borderLeft: '2px solid #EAE4DC', fontFamily: 'Playfair Display, serif', color: '#8A8798', animation: 'sagePulse 2s ease-in-out infinite' }}>
                  Sage is thinking…
                </p>
              </div>
            )}
          </div>

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="sticky bottom-0" style={{ background: 'linear-gradient(to top, #FBF9F6 70%, transparent)', paddingTop: '24px' }}>
        <div className="max-w-2xl mx-auto px-6 pb-6">
          <form
            onSubmit={handleSendMessage}
            className="flex items-center gap-2 rounded-2xl pl-5 pr-2 py-2"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #EAE4DC', boxShadow: '0 4px 20px rgba(26,26,46,0.04)' }}
          >
            <input
              ref={inputRef}
              type="text"
              placeholder="Say what's on your mind…"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              disabled={loading}
              className="flex-1 bg-transparent outline-none text-[15px] py-2 placeholder:text-[#A3A0AE]"
              style={{ fontFamily: 'Jost, sans-serif', color: '#1A1A2E' }}
              aria-label="Message Sage"
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={loading || !inputText.trim()}
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-opacity duration-200 hover:opacity-90 disabled:opacity-30"
              style={{ backgroundColor: '#1A1A2E' }}
              aria-label="Send message"
              data-testid="chat-send-button"
            >
              <ArrowUp className="w-5 h-5" strokeWidth={1.5} style={{ color: '#FBF9F6' }} />
            </button>
          </form>
          <p className="text-[11px] text-center mt-3" style={{ fontFamily: 'Jost, sans-serif', color: '#A3A0AE' }}>
            Sage offers coaching support, not clinical therapy. In crisis, call or text 988.
          </p>
        </div>
      </div>
      <CrisisFooter />
      <style>{`@keyframes sagePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
    </div>
  );
};
